import { useRoute, useRouter } from '#imports';

export default defineNuxtPlugin((nuxtApp) => {
    const CLICK_HANDLER = Symbol('v-app-link-click-handler');

    function logDebug(enabled, ...args) {
        if (enabled) console.log('[v-app-link]', ...args);
    }

    function resolveTemplate(template, routeParams, vars) {
        let failed = false;

        const withRouteParams = template.replace(/\(([^)]+)\)/g, (_, key) => {
            const val = routeParams?.[key];
            if (val == null || val === '') {
                failed = true;
                return '';
            }
            return encodeURIComponent(String(val));
        });

        const finalPath = withRouteParams.replace(/\{([^}]+)\}/g, (_, key) => {
            const val = vars?.[key];
            if (val == null || val === '') {
                failed = true;
                return '';
            }
            return encodeURIComponent(String(val));
        });

        return { path: finalPath, failed };
    }

    function getRouterRoute(binding) {
        // Best: use injected router/route from the component instance
        const inst = binding?.instance;
        const routerFromInstance = inst?.$router;
        const routeFromInstance = inst?.$route;

        if (routerFromInstance && routeFromInstance) {
            return { router: routerFromInstance, route: routeFromInstance, source: 'binding.instance' };
        }

        // Fallback: try composables (may or may not work depending on context)
        try {
            const router = useRouter();
            const route = useRoute();
            if (router && route) return { router, route, source: 'composables' };
        } catch (e) {
            // ignore; we'll handle missing below
        }

        return { router: null, route: null, source: 'none' };
    }

    function apply(el, binding) {
        const value = binding.value;
        const debug = !!binding.modifiers?.debug || !!value?.debug;

        const { router, route, source } = getRouterRoute(binding);
        logDebug(debug, 'hook:', binding?.instance ? 'directive' : 'directive', 'router/route source:', source);

        if (!router || !route) {
            // Can't navigate or resolve params reliably -> hide
            logDebug(debug, 'No router/route found; hiding element.');
            el.style.display = 'none';
            return;
        }

        if (!value || typeof value.path !== 'string') {
            logDebug(debug, 'Invalid binding value; expected { path: string, ... }');
            el.style.display = 'none';
            return;
        }

        const { path: template, replace, newTab, debug: _debugIgnored, ...vars } = value;

        const { path, failed } = resolveTemplate(template, route.params, vars);

        logDebug(debug, 'template:', template);
        logDebug(debug, 'route.params:', route.params);
        logDebug(debug, 'vars:', vars);
        logDebug(debug, 'resolved:', { path, failed });

        // Hide if missing anything
        if (failed) {
            el.style.display = 'none';
            return;
        }

        // Show if previously hidden
        el.style.display = '';

        const isAnchor = el.tagName.toLowerCase() === 'a';

        if (isAnchor) {
            el.setAttribute('href', path);
        } else {
            el.setAttribute('role', 'link');
            el.setAttribute('tabindex', '0');
            el.style.cursor = 'pointer';
        }

        // Remove old click handler
        const oldHandler = el[CLICK_HANDLER];
        if (oldHandler) el.removeEventListener('click', oldHandler);

        const handler = (e) => {
            logDebug(debug, 'CLICK', { path, replace, newTab, metaKey: e.metaKey, ctrlKey: e.ctrlKey });

            if (newTab || e.metaKey || e.ctrlKey) {
                window.open(path, '_blank', 'noopener,noreferrer');
                return;
            }

            // Prevent navigation reload for <a>
            if (isAnchor) e.preventDefault();

            const nav = replace ? router.replace(path) : router.push(path);

            // Log promise outcomes (helpful if router rejects)
            Promise.resolve(nav)
                .then(() => logDebug(debug, 'navigation ok'))
                .catch((err) => logDebug(debug, 'navigation error', err));
        };

        el[CLICK_HANDLER] = handler;
        el.addEventListener('click', handler);

        // Keyboard support for non-anchors
        el.onkeydown = (e) => {
            if (!isAnchor && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handler(new MouseEvent('click'));
            }
        };
    }

    nuxtApp.vueApp.directive('app-link', {
        mounted(el, binding) {
            apply(el, binding);
        },
        updated(el, binding) {
            apply(el, binding);
        },
        unmounted(el) {
            const oldHandler = el[CLICK_HANDLER];
            if (oldHandler) el.removeEventListener('click', oldHandler);
        }
    });
});
